/**
 * @name Umi route configuration
 * @description Only supports configuration items: path, component, routes, redirect, wrappers, name, and icon.
 * @param path  The path supports two placeholder formats. The first is a dynamic parameter in the form of :id, and the second is * as a wildcard. The wildcard can only appear at the end of the route string.
 * @param component The React component path to render when location and path match. Can be an absolute or relative path. If relative, it resolves from src/pages.
 * @param routes Configures child routes, typically used when you want to nest a layout component for multiple subpaths.
 * @param redirect Sets up route redirection.
 * @param wrappers Configures route wrapper components, which allow injecting extra functionality into a route. For example, route-level access control.
 * @param name Configure the route title. By default, it uses the value from the menu.ts i18n file under menu.xxxx. For example, if name is set to "login", it uses the value of menu.login as the title.
 * @param icon Configure the route icon. Refer to https://ant.design/components/icon/ for icon values. Remove any style suffix and use the component name in lowerCamelCase or UpperCamelCase. For example, <StepBackwardOutlined /> should be "stepBackward" or "StepBackward", and <UserOutlined /> should be "user" or "User".
 * @doc https://umijs.org/docs/guides/routes
 */

export default [
  {
    path: '/',
    redirect: '/home',
  },
  {
    path: '/home',
    name: 'home',
    icon: 'home',
    component: './home',
  },
  {
    path: '/payout-center',
    icon: 'dollar',
    name: 'payoutCenter',
    routes: [
      {
        path: '/payout-center',
        redirect: '/payout-center/ready-for-payout',
        hideInMenu: true,
      },
      {
        path: '/payout-center/ready-for-payout',
        name: 'readyForPayout',
        component: '@/pages/payout-center/ready-for-payout/readyForPayoutPage',
        access: 'page:payoutCenterReadyForPayout',
        icon: 'dollar',
      },
      {
        path: '/payout-center/batches-configuration',
        name: 'batchesConfiguration',
        component:
          '@/pages/payout-center/batches-configuration/batchesConfigurationPage',
        access: 'page:payoutCenterBatchesConfigurations',
        icon: 'setting',
      },
      {
        path: '/payout-center/batches-history',
        name: 'batchesHistory',
        component: '@/pages/payout-center/batches-history/batchesHistoryPage',
        access: 'page:payoutCenterBatchesHistory',
        icon: 'history',
      },
      {
        path: '/payout-center/batches-center',
        name: 'batchesCenter',
        component: '@/pages/payout-center/batches-center/batchesCenterPage',
        access: 'page:payoutCenterBatchesCenter',
      },
    ],
  },
  {
    path: '/reports/payment',
    name: 'payment',
    icon: 'dollar',
    routes: [
      {
        path: '/reports/payment',
        redirect: '/reports/payment/monthly-payin-due',
        hideInMenu: true,
      },
      {
        path: '/reports/payment/payin',
        redirect: '/reports/payment/monthly-payin-due',
        hideInMenu: true,
      },
      {
        path: '/reports/payment/monthly-payin-due',
        name: 'monthlyPayinDueMigrated',
        component:
          '@/pages/payment/monthly-payin-due-migrated/MonthlyPayinDueMigratedPage',
        access: 'page:monthlyPayinDueMigrated',
      },
      {
        path: '/reports/payment/payin/:uuid',
        name: 'payinDetails',
        component: '@/pages/payment/payin-report/PayinReportDetails',
        hideInMenu: true,
        access: 'page:payin',
      },
      {
        path: '/reports/payment/payout',
        name: 'payout',
        component: '@/pages/payment/payout-report/PayoutReport',
        access: 'page:payout',
      },
      {
        path: '/reports/payment/monthly-payout-due-migrated',
        name: 'monthlyPayoutDueMigrated',
        component:
          '@/pages/payment/monthly-payout-due-report/MonthlyPayoutDueReportPage',
        access: 'page:monthlyPayoutDueMigrated',
      },
      {
        path: '/reports/payment/payin-overrides',
        name: 'payinOverrides',
        component: '@/pages/payment/payin-overrides-report/payinOverridePage',
        access: 'page:payinOverride',
      },
      {
        path: '/reports/payment/payment-review',
        name: 'userPaymentReview',
        component: '@/pages/payment/user-payment-review/userPaymentReviewPage',
        access: 'page:userPaymentReview',
      },
      {
        path: '/reports/payment/payment-claim',
        name: 'paymentClaim',
        component: '@/pages/payment/payment-claim/paymentClaimPage',
        access: 'page:paymentClaimReport',
      },
      {
        path: '/reports/payment/payout-finance',
        name: 'payoutFinance',
        component:
          '@/pages/payment/payout-finance-report/payoutFinanceReportPage',
        access: 'page:releasingPayoutBatchReport',
        icon: 'accountBook',
      },
      {
        path: '/reports/payment/refund-requests',
        name: 'refundRequests',
        component: '@/pages/refund-requests/refundRequestsPage',
        icon: 'rollback',
        access: 'page:refundRequests',
      },
      {
        path: '/reports/payment/payin-claims',
        name: 'payinClaims',
        component: '@/pages/payin-claims/payinClaimsPage',
        icon: 'bank',
        access: 'page:settlementClaims',
      },
    ],
  },
  {
    path: '/reports/user',
    name: 'user',
    icon: 'user',
    routes: [
      {
        path: '/reports/user',
        redirect: '/reports/user/profile',
        hideInMenu: true,
      },
      {
        path: '/reports/user/profile',
        name: 'profile',
        component: '@/pages/user/user-profile/UserProfileReport',
        icon: 'user',
        access: 'page:userprofile',
      },
      {
        path: '/reports/user/profile/:id',
        name: 'profileDetail',
        component: '@/pages/user/user-profile/UserProfileDetail',
        hideInMenu: true,
        access: 'page:userprofile',
      },
      {
        path: '/reports/user/sanctioned-user',
        name: 'sanctionedUser',
        component: '@/pages/user/sanctioned-user/sanctionedUserPage',
        icon: 'user',
        access: 'page:sanctionedUsersReport',
      },
    ],
  },
  {
    path: '/reports/risk',
    name: 'risk',
    icon: 'warning',
    routes: [
      {
        path: '/reports/risk',
        redirect: '/reports/risk/aml-requests',
        hideInMenu: true,
      },
      {
        path: '/reports/risk/aml-requests',
        name: 'amlRequests',
        component: '@/pages/risk/aml-requests/amlRequestsPage',
        icon: 'fileSearch',
        access: 'page:amlReport',
      },
      {
        path: '/reports/risk/proof-of-income',
        name: 'proofOfIncome',
        component: '@/pages/risk/proof-of-income/proofOfIncomePage',
        icon: 'fileText',
        access: 'page:proofOfIncomeReport',
      },
      {
        path: '/reports/risk/watchlist',
        name: 'watchlist',
        component: '@/pages/risk/watchlist/watchlistPage',
        icon: 'eye',
        access: 'canViewWatchlist',
      },
    ],
  },
  {
    path: '/reports/dvs-requests',
    name: 'dvsRequests',
    icon: 'safetyCertificate',
    routes: [
      {
        path: '/reports/dvs-requests',
        redirect: '/reports/dvs-requests/mobile-validation',
        hideInMenu: true,
      },
      {
        path: '/reports/dvs-requests/mobile-validation',
        name: 'mobileValidation',
        component:
          '@/pages/dvs-requests/mobile-validation/mobileValidationPage',
        icon: 'mobile',
        access: 'page:mobile_validation_dvsReport',
      },
    ],
  },
  {
    path: '/reports/onboarding',
    name: 'onboarding',
    icon: 'folder',
    routes: [
      {
        path: '/reports/onboarding',
        redirect: '/reports/onboarding/agreement-contract',
        hideInMenu: true,
      },
      {
        path: '/reports/onboarding/agreement-contract',
        name: 'agreementContract',
        component:
          '@/pages/onboarding/agreement-contract/AgreementContractPage',
        access: 'page:agreementContractReport',
      },
      {
        path: '/reports/onboarding/agreement-contract/:id',
        name: 'agreementContractDetail',
        component:
          '@/pages/onboarding/agreement-contract/AgreementContractDetails',
        hideInMenu: true,
        access: 'page:agreementContractReport',
      },
      {
        path: '/reports/onboarding/national-id',
        name: 'nationalId',
        component: '@/pages/onboarding/national-id/nationalIdPage',
        access: 'page:nationalIdReport',
      },
    ],
  },
  {
    path: '/reports/card',
    name: 'card',
    icon: 'api',
    routes: [
      {
        path: '/reports/card',
        redirect: '/reports/card/ni-apis-consumption',
        hideInMenu: true,
        access: 'page:niApiConsumptionReport',
      },
      {
        path: '/reports/card/ni-apis-consumption',
        name: 'niApisConsumption',
        component:
          '@/pages/ni-reports/ni-apis-consumption/niApiConsumptionPage',
        icon: 'api',
        access: 'page:niApiConsumptionReport',
      },
      {
        path: '/reports/card/hubs-locations',
        name: 'hubsLocations',
        component: '@/pages/hubs-locations/hubsLocationsPage',
        access: 'page:ManageHubs',
        icon: 'environment',
      },
      {
        path: '/reports/card/cashback-transactions',
        name: 'cashbackTransactions',
        component: '@/pages/cashback-transactions/CashbackTransactions',
        access: 'page:uploadCashbackTransaction',
        icon: 'upload',
      },
    ],
  },
  {
    path: '/reports/card-center',
    name: 'cardCenter',
    icon: 'creditCard',
    routes: [
      {
        path: '/reports/card-center',
        redirect: '/reports/card-center/p2p-transfers',
        hideInMenu: true,
      },
      {
        path: '/reports/card-center/p2p-transfers',
        name: 'p2pTransfers',
        component: '@/pages/card-center/p2p-transfers/p2pTransfersPage',
        icon: 'transaction',
        access: 'page:cardTransfersReport',
      },
      {
        path: '/reports/card-center/p2p-requires-attention',
        name: 'p2pRequiresAttention',
        component:
          '@/pages/card-center/p2p-requires-attention/p2p-Requires-Attention-Transfers-Page',
        icon: 'warning',
        access: 'page:cardTransfersRequiresAttentionReport',
      },
    ],
  },
  {
    path: '/reports/card-shops-rewards',
    name: 'cardShopsRewards',
    icon: 'shop',
    routes: [
      {
        path: '/reports/card-shops-rewards',
        redirect: '/reports/card-shops-rewards/merchants',
        hideInMenu: true,
      },
      {
        path: '/reports/card-shops-rewards/merchants',
        name: 'merchants',
        component: '@/pages/card-shops-rewards/merchants/merchantsPage',
        access: 'page:merchants',
      },
      {
        path: '/reports/card-shops-rewards/merchants/add',
        name: 'merchantsAdd',
        component: '@/pages/card-shops-rewards/merchants/merchantFormPage',
        access: 'page:merchants',
        hideInMenu: true,
      },
      {
        path: '/reports/card-shops-rewards/merchants/edit/:id',
        name: 'merchantsEdit',
        component: '@/pages/card-shops-rewards/merchants/merchantFormPage',
        access: 'page:merchants',
        hideInMenu: true,
      },
      {
        path: '/reports/card-shops-rewards/offers',
        name: 'offers',
        component: '@/pages/card-shops-rewards/offers/offersPage',
        access: 'page:merchantsOffers',
      },
      {
        path: '/reports/card-shops-rewards/offers/add',
        name: 'offersAdd',
        component: '@/pages/card-shops-rewards/offers/offerFormPage',
        access: 'page:merchantsOffers',
        hideInMenu: true,
      },
      {
        path: '/reports/card-shops-rewards/offers/edit/:id',
        name: 'offersEdit',
        component: '@/pages/card-shops-rewards/offers/offerFormPage',
        access: 'page:merchantsOffers',
        hideInMenu: true,
      },
      {
        path: '/reports/card-shops-rewards/offers/duplicate/:id',
        name: 'offersDuplicate',
        component: '@/pages/card-shops-rewards/offers/offerFormPage',
        access: 'page:merchantsOffers',
        hideInMenu: true,
      },
      {
        path: '/reports/card-shops-rewards/cashback-offers',
        name: 'cashbackOffers',
        component:
          '@/pages/card-shops-rewards/cashback-offers/cardCashbackOffersPage',
        access: 'page:cardCashbackOffers',
      },
      {
        path: '/reports/card-shops-rewards/cashback-offers/add',
        name: 'cashbackOffersAdd',
        component:
          '@/pages/card-shops-rewards/cashback-offers/cardCashbackOfferFormPage',
        access: 'page:cardCashbackOffers',
        hideInMenu: true,
      },
      {
        path: '/reports/card-shops-rewards/cashback-offers/edit/:id',
        name: 'cashbackOffersEdit',
        component:
          '@/pages/card-shops-rewards/cashback-offers/cardCashbackOfferFormPage',
        access: 'page:cardCashbackOffers',
        hideInMenu: true,
      },
      {
        path: '/reports/card-shops-rewards/promo-code-campaigns',
        name: 'cardPromoCodeCampaigns',
        component:
          '@/pages/card-shops-rewards/promo-code-campaigns/cardPromoCodeCampaignsPage',
        access: 'page:cardPromoCodeCampaigns',
      },
      {
        path: '/reports/card-shops-rewards/promo-code-campaigns/new',
        name: 'cardPromoCodeCampaignsNew',
        component:
          '@/pages/card-shops-rewards/promo-code-campaigns/cardPromoCodeCampaignFormPage',
        access: 'page:cardPromoCodeCampaigns',
        hideInMenu: true,
      },
    ],
  },
  {
    path: '/reports/integration',
    name: 'integration',
    icon: 'team',
    routes: [
      {
        path: '/reports/integration',
        redirect: '/reports/integration/payin-merchants',
        hideInMenu: true,
      },
      {
        path: '/reports/integration/payin-merchants',
        name: 'payinMerchants',
        component:
          '@/pages/merchants/merchants-report/payinMerchantsReportPage',
        access: 'page:payinMerchants',
      },
      {
        path: '/reports/integration/history-analytics',
        name: 'historyAnalytics',
        component:
          '@/pages/merchants/history-and-analytics/HistoryAndAnalyticsPage',
        access: 'page:payinMerchants',
      },
      {
        path: '/reports/integration/user-kyc',
        name: 'userKyc',
        component: '@/pages/user-kyc-report/userKycReportPage',
        access: 'page:userKycReport',
      },
    ],
  },
  {
    path: '/reports/corporate',
    name: 'corporate',
    icon: 'bank',
    routes: [
      {
        path: '/reports/corporate',
        redirect: '/reports/corporate/organization',
        hideInMenu: true,
      },
      {
        path: '/reports/corporate/organization',
        name: 'organization',
        component:
          '@/pages/corporate/organization-report/organizationReportPage',
        access: 'page:organizationReport',
      },
      {
        path: '/reports/corporate/organization/:id',
        name: 'organizationDetails',
        component:
          '@/pages/corporate/organization-report/organizationDetailsPage',
        hideInMenu: true,
        access: 'page:organizationReport',
      },
    ],
  },
  {
    path: '/reports/admin',
    name: 'admin',
    icon: 'setting',
    routes: [
      {
        path: '/reports/admin',
        redirect: '/reports/admin/access-management',
        hideInMenu: true,
      },
      {
        path: '/reports/admin/access-management',
        name: 'accessManagement',
        component: '@/pages/access-management/AccessManagementReport',
        access: 'page:accessManagement',
        icon: 'setting',
      },
    ],
  },
  {
    path: '/reports/circles',
    name: 'circles',
    icon: 'copyright',
    routes: [
      {
        path: '/reports/circles',
        redirect: '/reports/circles/dashboard',
        hideInMenu: true,
      },
      {
        path: '/reports/circles/dashboard',
        name: 'dashboard',
        component: '@/pages/circles-dashboard/CircleDashboard',
        access: 'page:CircleDashBoard',
        icon: 'copyright',
      },
      {
        path: '/reports/circles/leave-bundle-requests',
        name: 'leaveBundleRequests',
        component:
          '@/pages/circles/leave-bundle-requests/LeaveBundleRequestsReport',
        access: 'page:leavebundlerequests',
      },
      {
        path: '/reports/circles/leave-requests',
        redirect: '/reports/circles/leave-bundle-requests',
        hideInMenu: true,
      },
      {
        path: '/reports/circles/circles-migrated',
        name: 'circlesMigrated',
        component: '@/pages/circles-migrated/circlesMigratedPage',
        access: 'page:circlesMigrated',
        icon: 'copyright',
      },
      {
        path: '/reports/circles/bundling-engine',
        name: 'bundlingEngine',
        component: '@/pages/bundling-engine/rulesPage',
        access: 'page:bundlingEngine',
      },
      {
        path: '/reports/circles/bundling-engine/new',
        name: 'bundlingEngineAdd',
        component: '@/pages/bundling-engine/createRulePage',
        access: 'page:bundlingEngine',
        hideInMenu: true,
      },
    ],
  },
  {
    path: '/reports/marketing',
    name: 'marketing',
    icon: 'gift',
    routes: [
      {
        path: '/reports/marketing',
        redirect: '/reports/marketing/manage-bundling-deeplink',
        hideInMenu: true,
      },
      {
        path: '/reports/marketing/manage-bundling-deeplink',
        name: 'deeplinkTypes',
        component: '@/pages/bundle-deeplink-types/bundleDeeplinkTypesPage',
        access: 'page:bundlingConfig',
        icon: 'gift',
      },
      {
        path: '/reports/marketing/marketing-campaigns',
        name: 'marketingCampaigns',
        component:
          '@/pages/marketing/marketing-campaigns/MarketingCampaignsPage',
        access: 'page:marketingCampaigns',
      },
      {
        path: '/reports/marketing/marketing-campaigns/new',
        component:
          '@/pages/marketing/marketing-campaigns/NewMarketingCampaignPage',
        access: 'page:marketingCampaigns',
        hideInMenu: true,
      },
      {
        path: '/reports/marketing/marketing-campaigns/edit/:id',
        component:
          '@/pages/marketing/marketing-campaigns/EditMarketingCampaignPage',
        access: 'page:marketingCampaigns',
        hideInMenu: true,
      },
      {
        path: '/reports/marketing/marketing-campaigns/success',
        component:
          '@/pages/marketing/marketing-campaigns/MarketingCampaignSuccessPage',
        access: 'page:marketingCampaigns',
        hideInMenu: true,
      },
      {
        path: '/reports/marketing/promo-campaigns',
        name: 'promoCampaigns',
        component: '@/pages/marketing/promo-campaigns/PromoCampaignsPage',
        access: 'page:promoCampaigns',
        icon: 'tags',
      },
      {
        path: '/reports/marketing/promo-campaigns/new',
        name: 'newPromoCampaign',
        component: '@/pages/marketing/promo-campaigns/PromoCampaignFormPage',
        access: 'page:promoCampaigns',
        hideInMenu: true,
      },
      {
        path: '/reports/marketing/promo-campaigns/duplicate/:id',
        name: 'duplicatePromoCampaign',
        component: '@/pages/marketing/promo-campaigns/PromoCampaignFormPage',
        access: 'page:promoCampaigns',
        hideInMenu: true,
      },
      {
        path: '/reports/marketing/marketing-goals',
        name: 'marketingGoals',
        component: '@/pages/marketing/marketing-goals/marketingGoalsPage',
        access: 'page:marketingGoal', // TODO: Change after proper handling for the new page permission seeders for page:marketingGoals
        icon: 'star',
      },
      {
        path: '/reports/marketing/marketing-carousels',
        name: 'marketingCarousels',
        component:
          '@/pages/marketing/marketing-carousels/marketingCarouselsPage',
        access: 'page:marketingCarousel',
        icon: 'picture',
      },
    ],
  },
  {
    path: '/quota-management',
    name: 'quotaManagement',
    icon: 'control',
    routes: [
      {
        path: '/quota-management',
        redirect: '/quota-management/hierarchy',
        hideInMenu: true,
      },
      {
        path: '/quota-management/dashboard',
        name: 'dashboard',
        component: '@/pages/quota-management/dashboard/dashboardPage',
        access: 'page:nodeInventoryDashboard',
        icon: 'dashboard',
      },
      {
        path: '/quota-management/my-quota',
        name: 'myQuota',
        component: '@/pages/quota-management/my-quota/myQuotaPage',
        access: 'page:myQuota',
        icon: 'profile',
      },
      {
        path: '/quota-management/hierarchy',
        name: 'hierarchy',
        component: '@/pages/quota-management/hierarchy/hierarchyPage',
        access: 'page:nodeInventoryHierarchy',
        icon: 'apartment',
      },
      {
        path: '/quota-management/slot-allocation',
        name: 'slotAllocation',
        component:
          '@/pages/quota-management/slot-allocation/slotAllocationPage',
        access: 'page:nodeInventoryAllocation',
        icon: 'partition',
      },
    ],
  },
  {
    path: '/pricing-management',
    name: 'pricingManagement',
    icon: 'fund',
    routes: [
      {
        path: '/pricing-management',
        redirect: '/pricing-management/rules',
        hideInMenu: true,
      },
      {
        path: '/pricing-management/segments',
        name: 'segments',
        component: '@/pages/pricing-management/segments/segmentsPage',
        access: 'page:pricingSegments',
        icon: 'team',
      },
      {
        path: '/pricing-management/segments/new',
        name: 'segmentsAdd',
        component: '@/pages/pricing-management/segments/segmentFormPage',
        access: 'page:pricingSegments',
        hideInMenu: true,
      },
      {
        path: '/pricing-management/segments/:id/edit',
        name: 'segmentsEdit',
        component: '@/pages/pricing-management/segments/segmentFormPage',
        access: 'page:pricingSegments',
        hideInMenu: true,
      },
      {
        path: '/pricing-management/distribution',
        name: 'distribution',
        component: '@/pages/pricing-management/distribution/distributionPage',
        access: 'page:pricingDistributions',
        icon: 'apartment',
      },
      {
        path: '/pricing-management/distribution/create',
        component:
          '@/pages/pricing-management/distribution/createDistributionPage',
        hideInMenu: true,
        access: 'page:pricingDistributions',
      },
      {
        path: '/pricing-management/distribution/edit/:id',
        component:
          '@/pages/pricing-management/distribution/editDistributionPage',
        hideInMenu: true,
        access: 'page:pricingDistributions',
      },
      {
        path: '/pricing-management/distribution/view/:id',
        component:
          '@/pages/pricing-management/distribution/viewDistributionPage',
        hideInMenu: true,
        access: 'page:pricingDistributions',
      },
      {
        path: '/pricing-management/rules',
        name: 'rules',
        component: '@/pages/pricing-management/rules/rulesPage',
        access: 'page:pricingRules',
        icon: 'profile',
      },
      {
        path: '/pricing-management/rules/create',
        component: '@/pages/pricing-management/rules/createRulePage',
        hideInMenu: true,
        access: 'page:pricingRules',
      },
      {
        path: '/pricing-management/rules/edit/:id',
        component: '@/pages/pricing-management/rules/editRulePage',
        hideInMenu: true,
        access: 'page:pricingRules',
      },
      {
        path: '/pricing-management/audit',
        name: 'audit',
        component: '@/pages/pricing-management/audit/auditPage',
        access: 'page:pricingAudit',
        icon: 'history',
      },
    ],
  },
  {
    path: '/reports/operations',
    name: 'operations',
    icon: 'deploymentUnit',
    routes: [
      {
        path: '/reports/operations',
        redirect: '/reports/operations/dead-letter-messages',
        hideInMenu: true,
      },
      {
        path: '/reports/operations/dead-letter-messages',
        name: 'deadLetterMessages',
        component:
          '@/pages/operations/dead-letter-messages/DeadLetterMessagesReport',
        access: 'page:deadLetterMessages',
        icon: 'inbox',
      },
      {
        path: '/reports/operations/dead-letter-messages/:id',
        name: 'deadLetterMessagesDetail',
        component:
          '@/pages/operations/dead-letter-messages/DeadLetterMessagesDetails',
        access: 'page:deadLetterMessages',
        hideInMenu: true,
      },
    ],
  },
  {
    path: '/moneyfellows-services',
    name: 'moneyfellowsServices',
    icon: 'customerService',
    routes: [
      {
        path: '/moneyfellows-services',
        redirect: '/moneyfellows-services/requests',
        hideInMenu: true,
      },
      {
        path: '/moneyfellows-services/requests',
        name: 'requests',
        component:
          '@/pages/moneyfellows-services/requests/ServiceRequestsReportPage',
        access: 'page:moneyfellowsServices',
        icon: 'profile',
      },
    ],
  },
  {
    path: '/user',
    layout: false,
    routes: [
      {
        path: '/user/login',
        layout: false,
        name: 'login',
        component: './user/login',
      },

      {
        component: '404',
        path: '/user/*',
      },
    ],
  },
  {
    path: '/auth',
    layout: false,
    routes: [
      {
        path: '/auth/callback',
        layout: false,
        name: 'authCallback',
        component: './auth/callback',
      },
      {
        path: '/auth/silent-check',
        layout: false,
        name: 'authSilentCheck',
        component: './auth/silent-check',
      },
      {
        path: '/auth/mock-login',
        layout: false,
        name: 'mockLogin',
        component: './auth/mock-login',
      },
    ],
  },
  {
    name: 'exception',
    icon: 'warning',
    path: '/exception',
    hideInMenu: true,

    routes: [
      {
        path: '/exception',
        redirect: '/exception/403',
        hideInMenu: true,
      },
      {
        name: '403',
        icon: 'smile',
        path: '/exception/403',
        component: './exception/403',
        hideInMenu: true,
      },
      {
        name: '404',
        icon: 'smile',
        path: '/exception/404',
        component: './exception/404',
      },
      {
        name: '500',
        icon: 'smile',
        path: '/exception/500',
        component: './exception/500',
      },
    ],
  },
  {
    path: '*',
    redirect: '/exception/404',
  },
];
